import React, { useEffect, useState, useRef } from 'react';
import { UserProfile } from '../types';
import { Session } from '@supabase/supabase-js';
import { getFeaturebaseJwt, loadFeaturebaseScript } from '../services/featurebaseService';

interface FeaturebasePanelProps {
  userProfile: UserProfile | null;
  session: Session | null;
}

const FEATUREBASE_ORGANIZATION = import.meta.env.VITE_FEATUREBASE_ORGANIZATION;

declare global {
  interface Window {
    Featurebase?: (...args: any[]) => void;
  }
}

export const FeaturebasePanel: React.FC<FeaturebasePanelProps> = ({ userProfile, session }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isWidgetReady, setIsWidgetReady] = useState(false);
  const embedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const initWidget = async () => {
      if (!session || !userProfile) {
        setError('Session ou profil utilisateur manquant');
        setIsLoading(false);
        return;
      }

      if (!FEATUREBASE_ORGANIZATION) {
        setError('Configuration Featurebase manquante (VITE_FEATUREBASE_ORGANIZATION)');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Attendre que le div data-featurebase-embed soit dans le DOM
        await new Promise(resolve => setTimeout(resolve, 100));

        // Charger le script SDK Featurebase si pas déjà chargé
        await loadFeaturebaseScript();

        // Attendre que le SDK soit disponible
        let retries = 0;
        while (typeof window.Featurebase !== 'function' && retries < 20) {
          await new Promise(resolve => setTimeout(resolve, 100));
          retries++;
        }

        if (typeof window.Featurebase !== 'function') {
          throw new Error('Featurebase SDK n\'a pas pu être chargé');
        }

        // Vérifier que le div data-featurebase-embed est présent
        const embedDiv = document.querySelector('[data-featurebase-embed]');
        if (!embedDiv) {
          throw new Error('Conteneur d\'embed non trouvé dans le DOM');
        }

        // Récupérer le JWT pour l'authentification
        const jwt = await getFeaturebaseJwt(session);

        // Initialiser le widget embarqué
        const initParams: any = {
          organization: FEATUREBASE_ORGANIZATION,
          embedOptions: {
            path: '/', // Page initiale : feedback board
            filters: '', // Filtres par défaut (optionnel)
          },
          stylingOptions: {
            theme: 'light', // Thème clair
            hideMenu: false, // Afficher le menu de navigation
            hideLogo: true, // Afficher le logo
          },
        };

        // Ajouter l'authentification JWT si disponible
        // Note: Si le SDK identify est déjà configuré (via initFeaturebase), 
        // l'authentification sera automatique et on n'a pas besoin de passer le JWT ici
        // Mais on peut le passer explicitement pour être sûr
        if (jwt) {
          initParams.user = {
            jwt: jwt,
          };
        }

        // Callback pour détecter quand le widget est prêt
        const callback = (err: any, result: any) => {
          console.log('[Featurebase Panel] Callback called', { err, result, action: result?.action });
          
          if (err) {
            console.error('[Featurebase Panel] Widget initialization error:', err);
            setError(`Erreur lors de l'initialisation du widget Featurebase: ${err.message || err}`);
            setIsLoading(false);
            return;
          }

          if (result?.action === 'widgetReady') {
            console.log('[Featurebase Panel] Widget ready');
            setIsWidgetReady(true);
            setIsLoading(false);
          } else {
            console.log('[Featurebase Panel] Callback received but action is not widgetReady:', result);
            // Si on reçoit un callback sans erreur, on considère que c'est OK
            setIsWidgetReady(true);
            setIsLoading(false);
          }
        };

        console.log('[Featurebase Panel] Initializing widget with params:', {
          organization: FEATUREBASE_ORGANIZATION,
          hasJwt: !!jwt,
          jwtLength: jwt?.length,
          embedDivFound: !!document.querySelector('[data-featurebase-embed]')
        });

        // Initialiser le widget
        // Note: Selon la doc, on peut aussi utiliser 'embed' au lieu de 'init_embed_widget'
        try {
          window.Featurebase!('init_embed_widget', initParams, callback);
          console.log('[Featurebase Panel] init_embed_widget called');
        } catch (initError) {
          console.error('[Featurebase Panel] Error calling init_embed_widget:', initError);
          setError(`Erreur lors de l'appel à init_embed_widget: ${initError}`);
          setIsLoading(false);
        }

        // Timeout de sécurité - vérifier périodiquement si le widget s'est chargé
        let timeoutCount = 0;
        const checkInterval = setInterval(() => {
          timeoutCount++;
          const embedDiv = document.querySelector('[data-featurebase-embed]');
          const hasContent = embedDiv && embedDiv.children.length > 0;
          
          console.log('[Featurebase Panel] Timeout check', {
            timeoutCount,
            isWidgetReady,
            hasEmbedDiv: !!embedDiv,
            hasContent,
            childrenCount: embedDiv?.children.length || 0
          });

          if (hasContent && !isWidgetReady) {
            console.log('[Featurebase Panel] Widget content detected, marking as ready');
            setIsWidgetReady(true);
            setIsLoading(false);
            clearInterval(checkInterval);
          } else if (timeoutCount >= 20) { // 20 * 500ms = 10 secondes
            console.warn('[Featurebase Panel] Timeout reached, widget may not have loaded');
            setIsLoading(false);
            if (!hasContent) {
              setError('Le widget Featurebase n\'a pas pu être initialisé après 10 secondes');
            }
            clearInterval(checkInterval);
          }
        }, 500);

      } catch (err) {
        console.error('[Featurebase Panel] Error initializing widget:', err);
        setError('Erreur lors du chargement du portail Featurebase');
        setIsLoading(false);
      }
    };

    initWidget();
  }, [session, userProfile]);

  // Toujours rendre le div data-featurebase-embed pour que le SDK puisse l'utiliser
  // Afficher un overlay de chargement si nécessaire
  return (
    <div className="h-full w-full bg-white relative" ref={embedRef}>
      {/* Conteneur pour le widget Featurebase embarqué */}
      <div 
        data-featurebase-embed 
        className="w-full h-full"
        style={{ minHeight: '600px' }}
      />
      
      {/* Overlay de chargement */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-50 bg-opacity-90 z-10">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-slate-300 border-t-amber-500 mb-4"></div>
            <p className="text-slate-600">Chargement...</p>
          </div>
        </div>
      )}

      {/* Overlay d'erreur */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-50 bg-opacity-95 z-20 p-8">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 border border-slate-200">
            <div className="text-center">
              <div className="bg-red-100 p-3 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Erreur de chargement</h3>
              <p className="text-slate-600 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="bg-amber-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-amber-600 transition-colors"
              >
                Réessayer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
